// CloudKeeper Org Services Portal API Service
// Shared by CloudTrail and (in Plan 3) Security Hub UI.
import { API_BASE_URL } from '../utils/constants';

// ===== Token + user storage =====
//
// Storage keys migrated from the legacy CloudTrail Portal: on first read,
// if a legacy `cloudtrail_token` / `cloudtrail_user` exists and a new key
// is absent, copy to the new key and clear the legacy one. After this
// migration step, only the `portal_*` keys are used.

function migrateLegacyStorage() {
  const legacyToken = localStorage.getItem('cloudtrail_token');
  if (legacyToken && !localStorage.getItem('portal_token')) {
    localStorage.setItem('portal_token', legacyToken);
  }
  const legacyUser = localStorage.getItem('cloudtrail_user');
  if (legacyUser && !localStorage.getItem('portal_user')) {
    localStorage.setItem('portal_user', legacyUser);
  }
  // Always clean up legacy keys once migrated.
  if (legacyToken) localStorage.removeItem('cloudtrail_token');
  if (legacyUser) localStorage.removeItem('cloudtrail_user');
}

export const getPortalToken = () => {
  migrateLegacyStorage();
  return localStorage.getItem('portal_token');
};

export const setPortalToken = (token) => {
  localStorage.setItem('portal_token', token);
};

export const removePortalToken = () => {
  localStorage.removeItem('portal_token');
  localStorage.removeItem('portal_user');
  localStorage.removeItem('cloudtrail_token');
  localStorage.removeItem('cloudtrail_user');
};

export const getPortalUser = () => {
  migrateLegacyStorage();
  const user = localStorage.getItem('portal_user');
  return user ? JSON.parse(user) : null;
};

export const setPortalUser = (user) => {
  localStorage.setItem('portal_user', JSON.stringify(user));
};

// Parse JWT token to get claims.
export const parsePortalToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing portal JWT token:', e);
    return null;
  }
};

// Generic fetch wrapper for portal API calls.
export const portalFetch = async (endpoint, options = {}) => {
  const token = getPortalToken();

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    removePortalToken();
    window.location.href = '/services';
    throw new Error('Authentication required');
  }

  const data = await response.json();

  if (!response.ok) {
    // The message is the only thing most callers need, but it is NOT the only
    // thing the backend sends. A refusal that names WHY it refused — IPAM's 409
    // carries a `blockers` array of {allocationId, cidr, resourceId,
    // resourceType, accountId} — used to have that structure thrown away here,
    // leaving the page with a sentence it could not turn into a table, and no
    // way to tell a 409 apart from a 500.
    //
    // So the parsed body and the status ride along on the Error. This is purely
    // additive: `err.message` is unchanged, and a caller that ignores the extra
    // fields behaves exactly as before.
    const err = new Error(data.message || data.error || 'Request failed');
    err.status = response.status;
    err.body = data;
    throw err;
  }

  return data;
};

// ===== CloudTrail service functions =====

// Get current user info
export const getCloudTrailMe = () => {
  return portalFetch('/api/v1/cloudtrail/me');
};

// List AWS accounts available for trail creation
export const listCloudTrailAccounts = () => {
  return portalFetch('/api/v1/cloudtrail/accounts');
};

// Create a new CloudTrail trail
export const createTrail = (data) => {
  return portalFetch('/api/v1/cloudtrail/trails', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// List all trails
export const listTrails = () => {
  return portalFetch('/api/v1/cloudtrail/trails');
};

// Get trail detail
export const getTrail = (trailId) => {
  return portalFetch(`/api/v1/cloudtrail/trails/${trailId}`);
};

// Update a trail
export const updateTrail = (trailId, data) => {
  return portalFetch(`/api/v1/cloudtrail/trails/${trailId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Delete a trail
export const deleteTrail = (trailId, deleteS3Bucket = false) => {
  return portalFetch(`/api/v1/cloudtrail/trails/${trailId}`, {
    method: 'DELETE',
    body: JSON.stringify({ deleteS3Bucket }),
  });
};

// Add accounts to an existing trail
export const addTrailAccounts = (trailId, accountIds) => {
  return portalFetch(`/api/v1/cloudtrail/trails/${trailId}/accounts`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });
};

// Remove accounts from an existing trail
export const removeTrailAccounts = (trailId, accountIds) => {
  return portalFetch(`/api/v1/cloudtrail/trails/${trailId}/accounts`, {
    method: 'DELETE',
    body: JSON.stringify({ accountIds }),
  });
};
